import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../../../utils/apiClient';
import { useAuth } from '../../../contexts/AuthContext';
import { 
  MessageSquare, 
  Lightbulb, 
  Target, 
  Sparkles,
  ArrowRight,
  Brain,
  User,
  BookOpen,
  Map,
  ChevronRight,
  HelpCircle,
  Eye
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Skeleton } from '../../../components/ui/skeleton';
import GuidelinesModal from '../../../components/GuidelinesModal';

// ============================================================
// MY TWIN - MATERIAL REDESIGN
// The visual center of the authenticated product
// ============================================================

export default function TwinOverview() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showGuidelines, setShowGuidelines] = useState(false);
  const [chatInput, setChatInput] = useState('');

  useEffect(() => {
    fetchData();
    
    const params = new URLSearchParams(location.search);
    if (params.get('onboarding') === 'complete') {
      setShowGuidelines(true);
    }
  }, [location]);

  const fetchData = async () => {
    try {
      const [profileRes, statsRes] = await Promise.all([
        apiClient.get('/personal/profile').catch(() => ({ data: null })),
        apiClient.get('/personal/twin-stats').catch(() => ({ data: null }))
      ]);
      
      setProfile(profileRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return t('greetings.morning') || 'Good morning';
    if (hour < 18) return t('greetings.afternoon') || 'Good afternoon';
    return t('greetings.evening') || 'Good evening';
  };

  const getTwinState = () => {
    if (!stats) return { label: t('twin.states.gettingStarted') || 'Getting Started', level: 0 };
    
    const evidenceCount = stats.evidenceCount || 0;
    const confirmedCount = stats.currentCount || 0;
    
    if (evidenceCount === 0) return { label: t('twin.states.gettingStarted') || 'Getting Started', level: 0 };
    if (confirmedCount === 0) return { label: t('twin.states.earlyFormation') || 'Early Formation', level: 1 };
    if (confirmedCount < 5) return { label: t('twin.states.developing') || 'Developing', level: 2 };
    return { label: t('twin.states.evolving') || 'Evolving', level: 3 };
  };

  const handleStartConversation = () => {
    if (chatInput.trim()) {
      navigate(`/personal/assistant?q=${encodeURIComponent(chatInput)}`);
    } else {
      navigate('/personal/assistant');
    }
  };

  const handleQuickAction = (prompt) => {
    navigate(`/personal/assistant?prompt=${prompt}`);
  };

  const isSubPage = location.pathname !== '/personal/twin';

  if (isSubPage) {
    return <Outlet />;
  }

  const twinState = getTwinState();

  if (loading) {
    return (
      <div className="p-6 lg:p-8 space-y-6" data-testid="twin-overview-loading">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-64 w-full rounded-3xl" />
        <Skeleton className="h-48 w-full rounded-2xl" />
      </div>
    );
  }

  return (
    <>
      <GuidelinesModal open={showGuidelines} onClose={() => setShowGuidelines(false)} />
      
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white" data-testid="twin-overview">
        {/* Main Content */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          
          {/* ============ TWIN HERO SECTION ============ */}
          <section className="relative mb-8">
            {/* Background gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-navy-800 via-navy-900 to-navy-800 rounded-3xl overflow-hidden">
              {/* Subtle pattern overlay */}
              <div className="absolute inset-0 opacity-10">
                <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                  <defs>
                    <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
                      <circle cx="5" cy="5" r="0.5" fill="white" />
                    </pattern>
                  </defs>
                  <rect width="100" height="100" fill="url(#grid)" />
                </svg>
              </div>
            </div>

            {/* Content */}
            <div className="relative px-6 py-10 lg:px-10 lg:py-12">
              <div className="flex flex-col lg:flex-row lg:items-center gap-8">
                
                {/* Left: Greeting & Twin Visual */}
                <div className="flex-1">
                  <p className="text-blue-200 text-sm font-medium mb-2">
                    {getGreeting()}, {user?.name?.split(' ')[0] || 'there'}
                  </p>
                  <h1 className="font-heading text-3xl lg:text-4xl font-bold text-white mb-4">
                    {t('twin.welcomeMessage') || 'Your Twin is here to think with you'}
                  </h1>
                  
                  {/* Twin State Badge */}
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur rounded-full">
                    <div className="flex gap-1">
                      {[0, 1, 2, 3].map((level) => (
                        <div 
                          key={level}
                          className={`w-2 h-2 rounded-full transition-colors ${
                            level <= twinState.level ? 'bg-blue-400' : 'bg-white/20'
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-sm text-white/90">{twinState.label}</span>
                  </div>
                </div>

                {/* Right: Twin Visualization */}
                <div className="flex-shrink-0">
                  <TwinAvatar stats={stats} />
                </div>
              </div>

              {/* Evidence Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-8">
                <StatBlock 
                  value={stats?.evidenceCount || 0} 
                  label={t('twin.evidence') || 'Evidence'} 
                  color="blue"
                />
                <StatBlock 
                  value={stats?.currentCount || 0} 
                  label={t('twin.current') || 'Current'} 
                  color="green"
                />
                <StatBlock 
                  value={stats?.conflictedCount || 0} 
                  label={t('twin.conflicted') || 'Conflicted'} 
                  color="amber"
                />
                <StatBlock 
                  value={stats?.unknownCount || 0} 
                  label={t('twin.unknown') || 'Unknown'} 
                  color="gray"
                />
              </div>
            </div>
          </section>

          {/* ============ ASK YOUR TWIN ============ */}
          <section className="mb-8">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="p-6">
                <h2 className="font-heading text-lg font-semibold text-gray-900 mb-4">
                  {t('twin.askAnything') || 'Ask your Twin anything...'}
                </h2>
                
                {/* Chat Input */}
                <div className="flex gap-3">
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleStartConversation()}
                      placeholder={t('twin.placeholder') || 'What would you like to explore?'}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-navy-500/30 focus:border-navy-500 transition-all"
                      data-testid="twin-chat-input"
                    />
                  </div>
                  <Button 
                    onClick={handleStartConversation}
                    className="bg-navy-800 hover:bg-navy-900 h-12 px-6"
                    data-testid="twin-chat-submit"
                  >
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </div>

                {/* Quick Actions */}
                <div className="flex flex-wrap gap-2 mt-4">
                  {[
                    { icon: Lightbulb, label: t('twin.actions.reflect') || 'Help me reflect', prompt: 'help-reflect' },
                    { icon: Brain, label: t('twin.actions.analyze') || 'Analyze a decision', prompt: 'analyze-decision' },
                    { icon: Target, label: t('twin.actions.goals') || 'Explore my goals', prompt: 'explore-goals' },
                    { icon: Sparkles, label: t('twin.actions.insights') || 'Show recent insights', prompt: 'recent-insights' },
                  ].map((action) => (
                    <button
                      key={action.prompt}
                      onClick={() => handleQuickAction(action.prompt)}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-full text-sm text-gray-700 transition-colors"
                      data-testid={`quick-action-${action.prompt}`}
                    >
                      <action.icon className="w-4 h-4 text-gray-500" />
                      {action.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* ============ REPRESENTATION AREAS ============ */}
          <section>
            <h2 className="font-heading text-lg font-semibold text-gray-900 mb-4">
              {t('twin.yourRepresentation') || 'Your representation'}
            </h2>
            
            <div className="grid sm:grid-cols-2 gap-4">
              {/* Identity */}
              <DomainCard
                to="/personal/identity"
                icon={User}
                title={t('nav.identity') || 'Identity'}
                description={t('twin.exploreIdentity') || 'How you describe and understand yourself'}
                stats={[
                  { label: 'Confirmed', value: stats?.currentCount || 0, color: 'green' },
                  { label: 'Uncertain', value: stats?.unknownCount || 0, color: 'gray' },
                ]}
                gradient="from-navy-50 to-blue-50"
                iconBg="bg-navy-100"
                iconColor="text-navy-700"
              />

              {/* Knowledge */}
              <DomainCard
                to="/personal/vault"
                icon={BookOpen}
                title={t('nav.knowledge') || 'Knowledge'}
                description={t('twin.yourDocuments') || 'Information and sources forming your knowledge space'}
                stats={[
                  { label: 'Sources', value: stats?.knowledgeCount || 0, color: 'blue' },
                ]}
                gradient="from-blue-50 to-indigo-50"
                iconBg="bg-blue-100"
                iconColor="text-blue-700"
              />

              {/* Reflections */}
              <DomainCard
                to="/personal/reflections"
                icon={Sparkles}
                title={t('nav.reflections') || 'Reflections'}
                description={t('twin.deeperThinking') || 'Thoughts and experiences captured in context'}
                stats={[
                  { label: 'Reflections', value: stats?.reflectionsCount || 0, color: 'purple' },
                ]}
                gradient="from-purple-50 to-pink-50"
                iconBg="bg-purple-100"
                iconColor="text-purple-700"
              />

              {/* Goals */}
              <DomainCard
                to="/personal/goals"
                icon={Target}
                title={t('nav.goals') || 'Goals'}
                description={t('twin.turnVision') || 'What you are intentionally working toward'}
                stats={[
                  { label: 'Active', value: stats?.goalsCount || 0, color: 'green' },
                ]}
                gradient="from-green-50 to-emerald-50"
                iconBg="bg-green-100"
                iconColor="text-green-700"
              />
            </div>

            {/* Life Map - Full Width */}
            <Link 
              to="/personal/life-map"
              className="mt-4 block p-6 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-2xl border border-teal-100 hover:border-teal-200 hover:shadow-md transition-all group"
              data-testid="link-lifemap"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-teal-100 flex items-center justify-center">
                    <Map className="w-6 h-6 text-teal-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{t('nav.lifeMap') || 'Life Map'}</h3>
                    <p className="text-sm text-gray-600">Your journey, changes and milestones across time</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
              </div>
            </Link>
          </section>

          {/* ============ WHY DOES MY TWIN SAY THIS ============ */}
          <section className="mt-8 p-6 bg-white rounded-2xl border border-gray-200">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Eye className="w-5 h-5 text-amber-700" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">
                  {t('twin.transparency') || 'Transparency'}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {t('twin.transparencyDesc') || 
                    'Your Twin shows you the evidence behind its responses. When evidence is uncertain or insufficient, your Twin will tell you — because UNKNOWN is a valid state.'}
                </p>
                <button 
                  onClick={() => navigate('/personal/assistant')}
                  className="mt-3 text-sm text-navy-700 hover:text-navy-900 font-medium inline-flex items-center gap-1"
                >
                  Learn more in a conversation
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </section>

        </div>
      </div>
    </>
  );
}

// ============================================================
// SUPPORTING COMPONENTS
// ============================================================

function TwinAvatar({ stats }) {
  const evidenceCount = stats?.evidenceCount || 0;
  
  return (
    <div className="relative w-32 h-32 lg:w-40 lg:h-40">
      {/* Orbital rings */}
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 160 160">
        <defs>
          <linearGradient id="twinRing1" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#60A5FA" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#A78BFA" stopOpacity="0.2" />
          </linearGradient>
        </defs>
        
        {/* Outer ring */}
        <circle 
          cx="80" cy="80" r="75" 
          fill="none" 
          stroke="url(#twinRing1)" 
          strokeWidth="1"
          strokeDasharray="4 4"
        >
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="0 80 80"
            to="360 80 80"
            dur="30s"
            repeatCount="indefinite"
          />
        </circle>
        
        {/* Inner ring */}
        <circle 
          cx="80" cy="80" r="55" 
          fill="none" 
          stroke="white" 
          strokeWidth="1"
          strokeOpacity="0.2"
        >
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="360 80 80"
            to="0 80 80"
            dur="20s"
            repeatCount="indefinite"
          />
        </circle>
        
        {/* Evidence nodes */}
        {evidenceCount > 0 && (
          <g>
            <circle cx="80" cy="5" r="3" fill="#60A5FA" opacity="0.8">
              <animateTransform attributeName="transform" type="rotate" from="0 80 80" to="360 80 80" dur="30s" repeatCount="indefinite" />
            </circle>
            <circle cx="155" cy="80" r="3" fill="#A78BFA" opacity="0.8">
              <animateTransform attributeName="transform" type="rotate" from="90 80 80" to="450 80 80" dur="30s" repeatCount="indefinite" />
            </circle>
          </g>
        )}
      </svg>
      
      {/* Center - Human representation */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-20 h-20 lg:w-24 lg:h-24 rounded-full bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-sm flex items-center justify-center">
          <svg width="48" height="60" viewBox="0 0 48 60" className="lg:w-14 lg:h-16">
            <defs>
              <linearGradient id="twinFill" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="white" stopOpacity="0.9" />
                <stop offset="100%" stopColor="white" stopOpacity="0.7" />
              </linearGradient>
            </defs>
            {/* Head */}
            <ellipse cx="24" cy="14" rx="11" ry="13" fill="url(#twinFill)" />
            {/* Shoulders */}
            <path d="M6 34 Q24 26 42 34 L44 55 Q24 60 4 55 Z" fill="url(#twinFill)" opacity="0.85" />
            {/* Inner glow */}
            <circle cx="24" cy="40" r="6" fill="#60A5FA" opacity="0.3">
              <animate attributeName="opacity" values="0.2;0.4;0.2" dur="3s" repeatCount="indefinite" />
            </circle>
          </svg>
        </div>
      </div>
    </div>
  );
}

function StatBlock({ value, label, color }) {
  const colors = {
    blue: 'text-blue-300',
    green: 'text-green-300',
    amber: 'text-amber-300',
    gray: 'text-gray-400',
  };
  
  return (
    <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-center">
      <div className={`text-2xl lg:text-3xl font-bold ${colors[color] || 'text-white'}`}>
        {value}
      </div>
      <div className="text-sm text-white/70 mt-1">{label}</div>
    </div>
  );
}

function DomainCard({ to, icon: Icon, title, description, stats, gradient, iconBg, iconColor }) {
  return (
    <Link
      to={to}
      className={`block p-5 bg-gradient-to-br ${gradient} rounded-2xl border border-gray-100 hover:border-gray-200 hover:shadow-md transition-all group`}
      data-testid={`domain-${title.toLowerCase()}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 rounded-xl ${iconBg} flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${iconColor}`} />
        </div>
        <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-gray-500 transition-colors" />
      </div>
      <h3 className="font-semibold text-gray-900 mb-1">{title}</h3>
      <p className="text-sm text-gray-600 mb-3 leading-relaxed">{description}</p>
      
      {stats && stats.length > 0 && (
        <div className="flex gap-3">
          {stats.map((stat, i) => (
            <div key={i} className="text-xs">
              <span className={`font-semibold ${
                stat.color === 'green' ? 'text-green-700' : 
                stat.color === 'blue' ? 'text-blue-700' : 
                stat.color === 'purple' ? 'text-purple-700' : 
                'text-gray-500'
              }`}>
                {stat.value}
              </span>
              <span className="text-gray-500 ml-1">{stat.label}</span>
            </div>
          ))}
        </div>
      )}
    </Link>
  );
}
